/**
 * TrendChart - Interactive line chart for daily trend data
 * 
 * Migrated from seo-engine-webtools-customer-module
 */

import { useState, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Box, Typography, Paper } from '@mui/material';
import { DailyMetric } from '@/types/customer';
import { CHART_COLORS } from '@/services/customer/constants';
import { formatCompactNumber, formatDateShort } from '@/services/customer/formatters';

interface Props {
  data: DailyMetric[];
  onDataPointClick?: (date: string, metric: string, value: number) => void;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
        {formatDateShort(label as string)}
      </Typography>
      {payload.map((entry, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: entry.color,
            }}
          />
          <Typography variant="caption">
            {entry.name}: <strong>{formatCompactNumber(entry.value as number)}</strong>
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

function TrendChart({ data, onDataPointClick }: Props) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          borderRadius: 1,
        }}
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  const handleClick = useCallback((chartData: any) => {
    if (onDataPointClick && chartData && chartData.activePayload) {
      const payload = chartData.activePayload[0].payload;
      const metric = chartData.activePayload[0].dataKey;
      onDataPointClick(payload.date, metric, payload[metric]);
    }
  }, [onDataPointClick]);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart
        data={data}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        onClick={handleClick}
        onMouseMove={(chartData) => {
          if (chartData && chartData.activeTooltipIndex !== undefined) {
            setActiveIndex(chartData.activeTooltipIndex);
          }
        }}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="date"
          tickFormatter={(value) => formatDateShort(value)}
          tick={{ fontSize: 12 }}
        />
        <YAxis
          tickFormatter={(value) => formatCompactNumber(value)}
          tick={{ fontSize: 12 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ cursor: 'default' }}
          iconType="line"
        />
        <Line
          type="monotone"
          dataKey="sessions"
          stroke={CHART_COLORS[0]}
          strokeWidth={activeIndex !== null ? 3 : 2}
          dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 6, strokeWidth: 2, cursor: onDataPointClick ? 'pointer' : 'default' }}
          name="Sessions"
          animationDuration={500}
        />
        <Line
          type="monotone"
          dataKey="users"
          stroke={CHART_COLORS[1]}
          strokeWidth={activeIndex !== null ? 3 : 2}
          dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 6, strokeWidth: 2, cursor: onDataPointClick ? 'pointer' : 'default' }}
          name="Users"
          animationDuration={500}
        />
        <Line
          type="monotone"
          dataKey="pageViews"
          stroke={CHART_COLORS[2]}
          strokeWidth={activeIndex !== null ? 3 : 2}
          dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
          activeDot={{ r: 6, strokeWidth: 2, cursor: onDataPointClick ? 'pointer' : 'default' }}
          name="Page Views"
          animationDuration={500}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default TrendChart;
